import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiError } from '../lib/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from './ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ShoppingBasket, Trash2, Receipt, ShieldCheck, Briefcase, Award, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const TIER_META = {
  audit:   { label: 'Audit',   icon: ShieldCheck, price: 14900 },
  listing: { label: 'Listing', icon: Briefcase,   price: 29900 },
  uplift:  { label: 'Uplift',  icon: Award,       price: 49900 },
};

export default function CommissionBasket({ items, onUpdate, onClear }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [batchTier, setBatchTier] = useState('audit');
  const [submitting, setSubmitting] = useState(false);

  const total = items.reduce((s, it) => s + (TIER_META[it.commission_type]?.price || 0), 0);
  const isInvoice = items.length >= 10;

  const setItemTier = (idx, tier) => {
    const next = [...items];
    next[idx] = { ...next[idx], commission_type: tier };
    onUpdate(next);
  };

  const remove = (idx) => onUpdate(items.filter((_, i) => i !== idx));

  const applyBatchTier = () => {
    onUpdate(items.map(it => ({ ...it, commission_type: batchTier })));
    toast.success(`All items set to ${TIER_META[batchTier].label}`);
  };

  const checkout = async () => {
    if (!items.length) return;
    setSubmitting(true);
    try {
      const { data } = await api.post('/billing/checkout/commission', {
        items: items.map(it => ({
          business_id: it.business_id,
          business_name: it.business_name,
          commission_type: it.commission_type,
        })),
        origin_url: window.location.origin,
      });
      onClear();
      if (data.mode === 'invoice') {
        toast.success(`Invoice created — total £${(data.total_pence / 100).toLocaleString('en-GB')}. Opening invoice page…`);
        window.open(data.hosted_invoice_url, '_blank');
        navigate('/account');
      } else {
        window.location.href = data.url;
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || 'Could not start checkout');
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="relative font-body text-xs"
          data-testid="open-basket-btn"
        >
          <ShoppingBasket className="w-3.5 h-3.5 mr-1.5" /> Basket
          {items.length > 0 && (
            <Badge className="ml-2 bg-brand-orange text-white border-0 font-body text-[10px] h-5 px-1.5" data-testid="basket-count">
              {items.length}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-lg flex flex-col" data-testid="basket-sheet">
        <SheetHeader>
          <SheetTitle className="font-display font-extrabold text-xl">Commission basket</SheetTitle>
          <p className="font-body text-xs text-ink/55">UKSPF-eligible interventions. Audit Report and Tier Uplift Certificate are included as evidence outputs.</p>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-center px-6">
            <div>
              <ShoppingBasket className="w-10 h-10 text-ink/20 mx-auto mb-3" strokeWidth={1.5} />
              <p className="font-body text-sm text-ink/50">Your basket is empty.</p>
              <p className="font-body text-xs text-ink/40 mt-1">Select businesses from the Priority Matrix and click the basket icon to add.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 my-4 p-3 rounded-[4px] bg-ink/[0.03]">
              <span className="font-body text-xs text-ink/60">Set all to:</span>
              <Select value={batchTier} onValueChange={setBatchTier}>
                <SelectTrigger className="h-8 flex-1 font-body text-xs" data-testid="batch-tier-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="audit">Audit — £149</SelectItem>
                  <SelectItem value="listing">Verification + Listing — £299</SelectItem>
                  <SelectItem value="uplift">Full Tier Uplift — £499</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={applyBatchTier} className="h-8 font-body text-xs" data-testid="apply-batch-tier-btn">Apply</Button>
            </div>

            <div className="flex-1 overflow-y-auto space-y-2 pr-1">
              {items.map((it, i) => {
                const meta = TIER_META[it.commission_type];
                const Icon = meta.icon;
                return (
                  <div key={`${it.business_id}-${i}`} className="rounded-[4px] border border-ink/10 p-3 flex items-start gap-3" data-testid={`basket-item-${i}`}>
                    <Icon className="w-4 h-4 text-brand-orange mt-1" strokeWidth={1.5} />
                    <div className="flex-1 min-w-0">
                      <p className="font-body text-sm font-semibold text-ink truncate">{it.business_name}</p>
                      <Select value={it.commission_type} onValueChange={(v) => setItemTier(i, v)}>
                        <SelectTrigger className="h-7 mt-1 font-body text-xs" data-testid={`item-tier-${i}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="audit">Audit — £149</SelectItem>
                          <SelectItem value="listing">Verification + Listing — £299</SelectItem>
                          <SelectItem value="uplift">Full Tier Uplift — £499</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="text-right">
                      <p className="font-display text-sm font-extrabold text-ink whitespace-nowrap">£{(meta.price / 100).toLocaleString('en-GB')}</p>
                      <Button variant="ghost" size="sm" onClick={() => remove(i)} className="h-7 px-2 mt-1 text-ink/40 hover:text-red-600" data-testid={`remove-item-${i}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>

            <SheetFooter className="border-t border-ink/10 pt-4 mt-4 flex-col gap-3 sm:flex-col">
              <div className="flex items-center justify-between w-full">
                <div>
                  <p className="font-body text-xs text-ink/50">{items.length} business{items.length !== 1 ? 'es' : ''} · {isInvoice ? 'Stripe Invoice (30-day BACS)' : 'Stripe Checkout'}</p>
                  {isInvoice && <p className="font-body text-[11px] text-info-blue mt-0.5">10+ businesses → invoice billed to your council billing contact.</p>}
                </div>
                <div className="text-right">
                  <p className="font-body text-[10px] text-ink/40 uppercase">Total</p>
                  <p className="font-display text-2xl font-extrabold text-ink" data-testid="basket-total">£{(total / 100).toLocaleString('en-GB')}</p>
                </div>
              </div>
              <div className="flex gap-2 w-full">
                <Button variant="outline" onClick={onClear} className="font-body text-sm" data-testid="clear-basket-btn">Clear</Button>
                <Button onClick={checkout} disabled={submitting} className="flex-1 bg-brand-orange hover:bg-brand-orange/90 text-white font-body text-sm" data-testid="checkout-basket-btn">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Receipt className="w-4 h-4 mr-1.5" />}
                  {isInvoice ? 'Generate invoice' : 'Checkout'}
                </Button>
              </div>
              <p className="font-body text-[10px] text-ink/40 text-center w-full leading-relaxed">
                Includes Digital Presence Audit Report. Listing & Uplift include 30-day re-assessment. Uplift includes 90-day Tier Uplift Certificate.
              </p>
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
